// Shared plumbing for the contact and pilot forms, captured by Netlify Forms.
//
// Netlify registers any <form data-netlify="true" name="..."> it finds in the
// built HTML, then intercepts a form-encoded POST whose `form-name` field
// matches. Email/Slack notifications and spam filtering (Akismet plus the
// `netlify-honeypot` field) are configured in the Netlify UI under
// Site configuration > Forms, not in code.
// https://docs.netlify.com/manage/forms/setup/#submit-javascript-rendered-forms-with-ajax

import validator from 'validator/es';

// Field table shared by BlockHeroContact and BlockPilotContact.
// required: [field name, id of the input to outline red when invalid]
// trimmed:  fields sent as their trimmed values (the ones validation checked)
export const CONTACT_FORM = {
  required: [
    ["firstName", "first-name"],
    ["lastName", "last-name"],
    ["email", "email"],
    ["company", "company"],
    ["region", "region"],
  ],
  trimmed: [
    "firstName",
    "lastName",
    "email",
    "phone",
    "company",
    "region",
    "message",
  ],
};

// Read what the browser actually holds. Autofill and mouse-paste never fire
// keyup, so mirroring inputs into React state would miss them, and it also
// lets the honeypot be read from the DOM rather than from state.
export const readForm = (form) => {
  const data = new FormData(form);
  const value = (name) => String(data.get(name) || "").trim();
  return { data, value };
};

// Clear red borders from a previous attempt so corrected fields reset.
export const clearFieldErrors = (form) => {
  form.querySelectorAll(".error").forEach((el) => {
    el.classList.remove("error");
  });
};

// Outline the first invalid required field and return false; true if all pass.
// Lookups are scoped to this form because the contact, pilot, and gated
// content forms reuse the same input ids.
export const validateFields = (form, { value }, required) => {
  for (const [name, id] of required) {
    const v = value(name);
    const ok =
      name === "email" ? validator.isEmail(v) : validator.isLength(v, { min: 1 });

    if (!ok) {
      const input = form.querySelector(`#${id}`);
      if (input) input.classList.add("error");
      return false;
    }
  }

  return true;
};

// POST to the site root, as Netlify's docs do. Posting to the current path
// risks a pretty-URL 301 (/page -> /page/) that the browser follows as a GET,
// which returns 200 with no submission recorded.
//
// Trimmed values are what validation checked, so a pasted " jane@agency.org "
// doesn't reach the notifications padded. pageUri is declared as a hidden
// input in the markup so Netlify registers the column at build time; its
// value is filled in here.
export const postNetlifyForm = ({ data, value }, trimmed) => {
  trimmed.forEach((name) => data.set(name, value(name)));
  data.set("pageUri", window.location.href);

  // Netlify labels each row in its submissions list with a field literally
  // named "name" (falling back to the message), so give it the sender's
  // name and email for an at-a-glance list.
  data.set(
    "name",
    `${value("firstName")} ${value("lastName")} (${value("email")})`
  );

  return fetch("/", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(data).toString(),
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`Form submission failed with status ${res.status}`);
    }
  });
};

// Open the optional post-submit redirect configured on the Prismic slice.
// "noopener" keeps the target page from reaching back into this tab via
// window.opener (reverse tabnabbing); the call then returns null, so there
// is nothing to focus. The submission has already been recorded by the time
// this runs, so a blocked popup must not surface as an error.
export const openRedirect = (primary) => {
  const redirect = primary && primary.submit_redirect_url;
  const redirectUrl = redirect && redirect.text;
  if (redirectUrl) {
    window.open(redirectUrl, "_blank", "noopener");
  }
};
